import React, { useEffect, useRef, useState } from 'react';
import { Table } from '../../components/table/table';
import { Label, TextInputLarge, TextInputSmall } from '../../components/forms/input';
import * as Button from '../../components/button/button';
import * as Page from '../../components/content/page-header';
import { FieldValues, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import usePageState from '../../hooks/use-page-state';
import { Modes } from '../../types/page-modes';
import { useQuery } from '../../hooks/use-query';
import { StaticTranslationKey } from './types/static-key';
import { usePostMutation, usePutMutation } from '../../hooks/use-mutations';
import { Translation } from './types/translation';
import { Checkbox2 } from '../../components/forms/checkbox';
import tenantService from '../../services/tenant-service';
import { alert } from '../../components/toast/toast';
import { useDefaultLanguage } from '../../hooks/use-default-language';

type KeyValue = {
  [key: string]: string;
};

export const StaticTranslationKeyEditor = (): JSX.Element => {
  const saveRef = useRef<HTMLButtonElement>();
  const [pageState, setPageState] = usePageState();
  const [transformedStaticKey, setTransformedStaticKey] = useState<KeyValue>();
  const { register, handleSubmit, getValues, setValue, formState: { errors } } = useForm();
  const { register: registerTranslation, handleSubmit: handleTranslationSubmit, setValue: setTranslationValue, getValues: getTranslationValues } = useForm();
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id as string;
  const defaultLang = useDefaultLanguage();
  const languages = useQuery({ path: '/languages/all' });
  const postMutation = usePostMutation({ path: '/static-keys' });
  const putMutation = usePutMutation({ path: `/static-keys/${id}` });
  const postMutationBatch = usePostMutation<Translation[]>({ path: '/translation' });
  const staticKey = useQuery<StaticTranslationKey>({ path: `/static-keys/${id}`, options: { enabled: id !== 'new' } });

  useEffect(() => {
    const transformedStaticKey: KeyValue = {};
    staticKey.data?.translations?.forEach(translation => transformedStaticKey[translation.lang] = translation.id as string);
    setTransformedStaticKey(transformedStaticKey);
    setValue('key', staticKey.data?.key);
    setValue('description', staticKey.data?.description);
    if (tenantService.isGoldMaster()) {
      setValue('locked', staticKey.data?.locked);
    }
    staticKey?.data?.translations?.forEach?.((translation): void => {
      setTranslationValue(`translations[${translation.lang}]`, translation.value);
    });
  }, [staticKey.data]);

  useEffect(() => {
    if (postMutation.isSuccess || putMutation.isSuccess) {
      navigate('/internationalization/static-translation-keys');
    }
  }, [postMutation.isLoading, putMutation.isLoading]);

  const onSubmit = (data: FieldValues): void => {
    const translations = Object.entries(getTranslationValues().translations as KeyValue)
      .map(([key, value]) => ({ lang: key, value: value, id: transformedStaticKey?.[key] }));
    const { id, ...otherProps } = { ...data as StaticTranslationKey, id: params.id, translations };
    !id || id === 'new' ? postMutation.mutate(otherProps) : putMutation.mutate(otherProps);
  };

  const onTranslate = async (data: FieldValues): Promise<void> => {
    if (getValues().key.trim() === '') {
      saveRef.current?.click();
      return;
    }

    setPageState(Modes.FETCHING);

    const translations = Object.entries(data.translations as KeyValue)
      .filter(([, value]) => value === '')
      .map(([key]): Translation => {
        return {
          'key': key,
          'from': defaultLang,
          'to': key,
          'phrase': data.translations[defaultLang],
        };
      });
    if (translations.length) {
      try {
        const result = await postMutationBatch.mutateAsync(translations);
        result?.forEach?.((item: Translation) => {
          setTranslationValue(`translations[${item.key}]`, item.translation);
        });
      } catch {
        alert.success('Translation failed');
      }
    }

    setPageState(Modes.BROWSE);
  };

  const columns = [
    { accessor: 'name', Header: 'Language', sort: true },
    { accessor: 'code', Header: 'Language Code', sort: true },
    {
      accessor: 'value',
      Header: 'Value',
      Cell: (item: any): JSX.Element => <TextInputSmall {...registerTranslation(`translations[${item.row.original.code as string}`, { required: item.row.original.code === defaultLang })} />
    }
  ];

  return <div>
    <Page.Header>
      <Page.Tenant />
      <Page.Title>{id === 'new' ? 'Add' : 'Edit'} Static Translation Key</Page.Title>
      <Page.SubTitle>Add a static translation key using the form below. Enter the english value and use the "Translate" button to Translate the value into other languages.</Page.SubTitle>
    </Page.Header>
    <div className="row">
      <div className="col-9 bg-light left-col">
        <form id="static-translation-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            <Label name="key">Translation Key*</Label>
            <TextInputLarge {...register('key', { required: 'You must provide a value for Translation Key.' })} errors={errors} placeholder="Provide a translation key" disabled={!tenantService.isGoldMaster()} />
          </div>
          <div className="mb-4">
            <Label name="description">Description</Label>
            <TextInputLarge {...register('description')} placeholder="Provide a description (optional)" disabled={!tenantService.isGoldMaster()} />
          </div>
          {tenantService.isGoldMaster() && (
            <div className="mb-4">
              <Checkbox2 {...register('locked')} defaultChecked={getValues().locked} />
            </div>
          )}
        </form>
        <form id="translation-form" onSubmit={handleTranslationSubmit(onTranslate)}>
          <Table columns={columns} rows={languages.data as Translation[] || []} />
        </form>
      </div>
      <div className="col-3 d-flex flex-column">
        <div className="px-5">
          <div className="mb-3">
            <Button.Primary form="static-translation-form" type="submit" ref={saveRef} disabled={postMutation.isLoading || putMutation.isLoading || pageState.mode === Modes.FETCHING}>
              Save
            </Button.Primary>
          </div>
          <div className="mb-3">
            <Button.LightBlack form="translation-form" type="submit" disabled={postMutation.isLoading || putMutation.isLoading || pageState.mode === Modes.FETCHING}>
              Translate
            </Button.LightBlack>
          </div>
          <div className="mb-3">
            <Button.Tertiary onClick={(): void => navigate('/internationalization/static-translation-keys')} disabled={postMutation.isLoading || pageState.mode === Modes.FETCHING}>
              Cancel
            </Button.Tertiary>
          </div>
        </div>
      </div>
    </div>
  </div>;
};
